import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  AlertTriangle, Calendar as CalendarIcon, Clock, Package, 
  CheckCircle, Edit2, X, Save, ExternalLink
} from "lucide-react";
import { formatDataItaliana } from "@/utils/dateUtils";
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { it } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '../styles/calendar.css';

const DnDCalendar = withDragAndDrop(Calendar);

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 1 }),
  getDay,
  locales: { 'it': it },
});

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CronoOrdiniPage({ user, logout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ordiniScaduti, setOrdiniScaduti] = useState([]);
  const [ordiniInScadenza, setOrdiniInScadenza] = useState([]);
  const [ordiniFuturi, setOrdiniFuturi] = useState([]);
  const [ordiniSenzaData, setOrdiniSenzaData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingDate, setEditingDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(true);
  
  // Stati per il calendario
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarView, setCalendarView] = useState('month');

  useEffect(() => {
    fetchOrdini();
  }, []);

  const fetchOrdini = async () => {
    setLoading(true);
    try {
      const [cronoRes, senzaDataRes] = await Promise.all([
        axios.get(`${API}/crono-ordini`, { withCredentials: true }),
        axios.get(`${API}/crono-ordini/senza-data`, { withCredentials: true })
      ]);
      
      setOrdiniScaduti(cronoRes.data.scaduti);
      setOrdiniInScadenza(cronoRes.data.in_scadenza);
      setOrdiniFuturi(cronoRes.data.futuri);
      setOrdiniSenzaData(senzaDataRes.data);
    } catch (error) {
      console.error("Error fetching crono ordini:", error);
      toast.error("Errore nel caricamento degli ordini");
    } finally {
      setLoading(false);
    }
  };

  // Prepara eventi per il calendario
  const calendarEvents = [
    ...ordiniScaduti,
    ...ordiniInScadenza,
    ...ordiniFuturi
  ].map(ordine => ({
    id: `${ordine.commessa_id}-${ordine.articolo_index}`,
    title: `${ordine.cliente_nome} ${ordine.cliente_cognome} - ${ordine.articolo}`,
    start: new Date(ordine.data_prevista),
    end: new Date(ordine.data_prevista),
    resource: ordine,
  }));

  // Gestori per il calendario
  const handleNavigate = (newDate) => {
    setCalendarDate(newDate);
  };

  const handleViewChange = (newView) => {
    setCalendarView(newView);
  };
  const handleEventDrop = useCallback(async ({ event, start, end }) => {
    const ordine = event.resource;
    const newDate = format(start, 'yyyy-MM-dd');
    
    try {
      await axios.put(
        `${API}/commesse-pose/${ordine.commessa_id}/articolo/${ordine.articolo_index}/data-ordine`,
        { data_prevista_ordine: newDate },
        { withCredentials: true }
      );
      
      toast.success("Data aggiornata con successo");
      fetchOrdini();
    } catch (error) {
      console.error("Error updating date:", error);
      toast.error("Errore nell'aggiornamento della data");
    }
  }, []);

  // Personalizza lo stile degli eventi in base allo stato
  const eventStyleGetter = (event) => {
    const ordine = event.resource;
    let backgroundColor = '#10b981'; // verde default
    
    if (ordine.giorni_ritardo !== undefined) {
      backgroundColor = '#ef4444'; // rosso per scaduti
    } else if (ordine.giorni_mancanti !== undefined && ordine.giorni_mancanti <= 7) {
      backgroundColor = '#f97316'; // arancione per urgenti
    }
    
    return {
      style: {
        backgroundColor,
        borderRadius: '4px',
        opacity: 0.8,
        color: 'white',
        border: 'none',
        display: 'block',
      }
    };
  };

  const handleEditDate = (ordine) => {
    setEditingId(`${ordine.commessa_id}-${ordine.articolo_index}`);
    setEditingDate(ordine.data_prevista);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingDate("");
  };

  const handleSaveDate = async (ordine) => {
    if (!editingDate) {
      toast.error("Inserisci una data valida");
      return;
    }

    try {
      await axios.put(
        `${API}/commesse-pose/${ordine.commessa_id}/articolo/${ordine.articolo_index}/data-ordine`,
        { data_prevista_ordine: editingDate },
        { withCredentials: true }
      );
      
      toast.success("Data aggiornata con successo");
      setEditingId(null);
      setEditingDate("");
      fetchOrdini();
    } catch (error) {
      console.error("Error updating date:", error);
      toast.error("Errore nell'aggiornamento della data");
    }
  };

  const renderOrdineRow = (ordine, index) => {
    const isEditing = editingId === `${ordine.commessa_id}-${ordine.articolo_index}`;
    const giorniMancanti = ordine.giorni_mancanti || ordine.giorni_ritardo;

    return (
      <tr 
        key={`${ordine.commessa_id}-${ordine.articolo_index}`}
        className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
      >
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/commesse-pose/${ordine.commessa_id}`)}
              className="text-blue-600 hover:text-blue-800 hover:underline font-medium bg-transparent"
              title="Apri dettaglio commessa"
            >
              {ordine.cliente_nome} {ordine.cliente_cognome}
            </button>
            <ExternalLink className="w-3 h-3 text-slate-400" />
          </div>
        </td>
        <td className="py-3 px-4">
          <span className="text-slate-700">{ordine.articolo}</span>
        </td>
        <td className="py-3 px-4">
          {isEditing ? (
            <Input
              type="date"
              value={editingDate}
              onChange={(e) => setEditingDate(e.target.value)}
              className="w-40"
            />
          ) : (
            <span className="text-slate-600">
              {formatDataItaliana(ordine.data_prevista)}
            </span>
          )}
        </td>
        <td className="py-3 px-4">
          {ordine.giorni_ritardo !== undefined ? (
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
              {ordine.giorni_ritardo} {ordine.giorni_ritardo === 1 ? 'giorno' : 'giorni'} di ritardo
            </span>
          ) : ordine.giorni_mancanti !== undefined ? (
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              giorniMancanti <= 7 
                ? "bg-orange-100 text-orange-700" 
                : "bg-green-100 text-green-700"
            }`}>
              {ordine.giorni_mancanti === 0 
                ? "Oggi" 
                : `${ordine.giorni_mancanti} ${ordine.giorni_mancanti === 1 ? 'giorno' : 'giorni'}`
              }
            </span>
          ) : null}
        </td>
        <td className="py-3 px-4">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <>
                <Button
                  size="sm"
                  onClick={() => handleSaveDate(ordine)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Save className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCancelEdit}
                >
                  <X className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEditDate(ordine)}
                title="Modifica data"
              >
                <Edit2 className="w-4 h-4" />
              </Button>
            )}
          </div>
        </td>
      </tr>
    );
  };

  if (loading) {
    return (
      <PageLayout user={user} logout={logout} testId="crono-ordini-page">
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout user={user} logout={logout} testId="crono-ordini-page">
        <div className="flex-1 overflow-auto p-4 lg:p-6">
          {/* Header - Compatto */}
          <div className="mb-4 flex-shrink-0">
            <h1 className="text-xl lg:text-2xl font-bold text-slate-900 mb-1">
              Crono Ordini
            </h1>
            <p className="text-slate-600 text-sm hidden lg:block">
              Monitora le scadenze degli ordini e aggiorna le date previste
            </p>
          </div>

          {/* Ordini senza Data - PRIORITÀ CRITICA */}
          <Card className="mb-6 border-2 border-slate-300">
            <CardHeader className="bg-slate-50">
              <CardTitle className="flex items-center gap-2 text-slate-800">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
                ⚪ Ordini senza Data ({ordiniSenzaData.length}) - ⚠️ CRITICO
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {ordiniSenzaData.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-slate-600">Tutti gli ordini hanno una data prevista</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Cliente</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Articolo</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Data Prevista</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordiniSenzaData.map((ordine, idx) => {
                        const isEditing = editingId === `${ordine.commessa_id}-${ordine.articolo_index}`;
                        
                        return (
                          <tr 
                            key={`${ordine.commessa_id}-${ordine.articolo_index}`}
                            className="border-b border-slate-200 hover:bg-slate-50 transition-colors"
                          >
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => navigate(`/commesse-pose/${ordine.commessa_id}`)}
                                  className="text-blue-600 hover:text-blue-800 hover:underline font-medium bg-transparent"
                                  title="Apri dettaglio commessa"
                                >
                                  {ordine.cliente_nome} {ordine.cliente_cognome}
                                </button>
                                <ExternalLink className="w-3 h-3 text-slate-400" />
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-slate-700">{ordine.articolo}</span>
                            </td>
                            <td className="py-3 px-4">
                              {isEditing ? (
                                <Input
                                  type="date"
                                  value={editingDate}
                                  onChange={(e) => setEditingDate(e.target.value)}
                                  className="w-40"
                                />
                              ) : (
                                <span className="text-slate-400 italic">
                                  Non impostata
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                {isEditing ? (
                                  <>
                                    <Button
                                      size="sm"
                                      onClick={() => handleSaveDate(ordine)}
                                      className="bg-green-600 hover:bg-green-700"
                                    >
                                      <Save className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleCancelEdit}
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEditDate(ordine)}
                                    title="Imposta data"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ordini Scaduti */}
          <Card className="mb-6 border-2 border-red-200">
            <CardHeader className="bg-red-50">
              <CardTitle className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-5 h-5" />
                🔴 Ordini Scaduti ({ordiniScaduti.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {ordiniScaduti.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <p className="text-slate-600">Nessun ordine scaduto</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Cliente</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Articolo</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Data Prevista</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Stato</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordiniScaduti.map((ordine, idx) => renderOrdineRow(ordine, idx))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ordini in Scadenza (0-20 giorni) */}
          <Card className="mb-6 border-2 border-orange-200">
            <CardHeader className="bg-orange-50">
              <CardTitle className="flex items-center gap-2 text-orange-800">
                <Clock className="w-5 h-5" />
                🟠 Ordini in Scadenza (0-20 giorni) ({ordiniInScadenza.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {ordiniInScadenza.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">Nessun ordine in scadenza</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Cliente</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Articolo</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Data Prevista</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Stato</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordiniInScadenza.map((ordine, idx) => renderOrdineRow(ordine, idx))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Ordini Futuri (oltre 20 giorni) */}
          <Card className="border-2 border-green-200">
            <CardHeader className="bg-green-50">
              <CardTitle className="flex items-center gap-2 text-green-800">
                <CalendarIcon className="w-5 h-5" />
                🟢 Ordini Futuri (oltre 20 giorni) ({ordiniFuturi.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {ordiniFuturi.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">Nessun ordine futuro</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Cliente</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Articolo</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Data Prevista</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Stato</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ordiniFuturi.map((ordine, idx) => renderOrdineRow(ordine, idx))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Vista Calendario */}
          <Card className="mt-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5" />
                  Vista Calendario
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCalendar(!showCalendar)}
                >
                  {showCalendar ? "Nascondi" : "Mostra"}
                </Button>
              </div>
            </CardHeader>
            {showCalendar && (
              <CardContent>
                <div className="bg-white rounded-lg p-4" style={{ height: '600px' }}>
                  <DnDCalendar
                    localizer={localizer}
                    events={calendarEvents}
                    startAccessor="start"
                    endAccessor="end"
                    date={calendarDate}
                    view={calendarView}
                    onNavigate={handleNavigate}
                    onView={handleViewChange}
                    culture="it"
                    messages={{
                      next: "Avanti",
                      previous: "Indietro",
                      today: "Oggi",
                      month: "Mese",
                      week: "Settimana",
                      day: "Giorno",
                      agenda: "Agenda",
                      date: "Data",
                      time: "Ora",
                      event: "Ordine",
                      allDay: "Tutto il giorno",
                      work_week: "Settimana lavorativa",
                      yesterday: "Ieri",
                      tomorrow: "Domani",
                      noEventsInRange: "Nessun ordine in questo periodo",
                      showMore: (total) => `+ altri ${total}`
                    }}
                    eventPropGetter={eventStyleGetter}
                    onEventDrop={handleEventDrop}
                    resizable={false}
                    views={['month', 'week', 'day', 'agenda']}
                    style={{ height: '100%' }}
                    onSelectEvent={(event) => navigate(`/commesse-pose/${event.resource.commessa_id}`)}
                  />
                </div>
                <div className="mt-4 flex gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-red-500 rounded"></div>
                    <span>Scaduti</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-orange-500 rounded"></div>
                    <span>Urgenti (0-7 giorni)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-500 rounded"></div>
                    <span>In programma</span>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600">
                  💡 <strong>Suggerimento:</strong> Trascina gli ordini su una nuova data nel calendario per aggiornare la data prevista.
                </p>
              </CardContent>
            )}
          </Card>
        </div>
    </PageLayout>
  );
}
